import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Folder, LogOut, Plus, Trash2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { buildExpandedAccess } from "~/pages/manifold-nextgen/components/share/AccessDetailsView";
import { useProjectEntityRemoval } from "~/pages/manifold-nextgen/hooks/useProjectEntityRemoval";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { ROUTE_NEXTGEN_VIEW, nextGenDatasetDetailPath } from "~/router/constants";
import { AgentStatus } from "~/services/agents";
import { deleteProject, leaveProject, projectListPath } from "~/services/projects";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { LeaveProjectDialog } from "./LeaveProjectDialog";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

export const ProjectSettings = () => {
  const { projectId, project, isOwner, attachedDatasets, attachedAgents } = useProjectContext();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addDatasetsOpen, setAddDatasetsOpen] = useState(false);
  const [addAgentsOpen, setAddAgentsOpen] = useState(false);

  const { collaborators, accessLevels } = useProjectShare(projectId);
  const memberCount = collaborators
    ? buildExpandedAccess(collaborators.users, collaborators.groups, accessLevels).length
    : undefined;

  const { handleRemoveDataset, handleRemoveAgent, isRemovingDataset, isRemovingAgent } =
    useProjectEntityRemoval(projectId, attachedDatasets, attachedAgents);

  const handleDelete = useCallback(async () => {
    try {
      await deleteProject(projectId);
      await mutate(projectListPath());
      showSuccess(`"${project.name}" archived`);
      navigate(nextgenProjectsListPath());
    } catch {
      showError("Failed to archive project. Please try again.");
    }
  }, [projectId, project.name, navigate, showSuccess, showError]);

  // Throws on failure so LeaveProjectDialog can surface the reason inline (e.g.
  // the "keep at least one owner" 409 when the caller is the only owner).
  const handleLeave = useCallback(async () => {
    await leaveProject(projectId);
    await mutate(projectListPath());
    navigate(nextgenProjectsListPath());
  }, [projectId, navigate]);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <SettingsSection
          title="Datasets"
          headerAction={
            isOwner && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setAddDatasetsOpen(true)}
              >
                <Plus className="size-4" />
                Add Datasets
              </Button>
            )
          }
        >
          {attachedDatasets === undefined || attachedDatasets.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm italic text-muted-foreground">
              No datasets attached.
            </p>
          ) : (
            attachedDatasets.map((dataset) => (
              <SettingsRow
                key={dataset.id}
                icon={<Folder className="size-5 shrink-0 text-muted-foreground" />}
                title={dataset.title}
                description={dataset.description}
                href={nextGenDatasetDetailPath(dataset.id)}
              >
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                    onClick={() => handleRemoveDataset(dataset.id)}
                    disabled={isRemovingDataset}
                    aria-label={`Remove ${dataset.title}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </SettingsRow>
            ))
          )}
        </SettingsSection>

        <SettingsSection
          title="Agents"
          headerAction={
            isOwner && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setAddAgentsOpen(true)}
              >
                <Plus className="size-4" />
                Add Agents
              </Button>
            )
          }
        >
          {attachedAgents === undefined || attachedAgents.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm italic text-muted-foreground">
              No agents attached.
            </p>
          ) : (
            attachedAgents.map((agent) => (
              <SettingsRow
                key={agent.id}
                icon={<AgentAvatar agent={agent} size={32} />}
                title={agent.display_name}
                description={agent.description}
                href={
                  agent.status !== AgentStatus.DELETED
                    ? `${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`
                    : undefined
                }
              >
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                    onClick={() => handleRemoveAgent(agent.id)}
                    disabled={isRemovingAgent}
                    aria-label={`Remove ${agent.display_name}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </SettingsRow>
            ))
          )}
        </SettingsSection>

        <SettingsSection title="Danger Zone" danger>
          <SettingsRow
            title="Leave project"
            description="You'll lose access to this project unless it's shared with you again."
          >
            <Button variant="outline" className="w-32" onClick={() => setLeaveOpen(true)}>
              <LogOut className="size-4" />
              Leave
            </Button>
          </SettingsRow>
          {isOwner && (
            <SettingsRow
              title="Archive project"
              description="Archive and hide this project. An admin can restore if necessary."
            >
              <Button variant="destructive" className="w-32" onClick={() => setDeleteOpen(true)}>
                <Archive className="size-4" />
                Archive
              </Button>
            </SettingsRow>
          )}
        </SettingsSection>
      </div>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectName={project.name}
        memberCount={memberCount}
        onConfirm={handleDelete}
      />
      <LeaveProjectDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        projectName={project.name}
        onConfirm={handleLeave}
      />
      <ProjectDatasetsDialog
        projectId={projectId}
        open={addDatasetsOpen}
        onOpenChange={setAddDatasetsOpen}
        attachedIds={(attachedDatasets ?? []).map((d) => d.id)}
      />
      <ProjectAgentsDialog
        projectId={projectId}
        open={addAgentsOpen}
        onOpenChange={setAddAgentsOpen}
        attached={attachedAgents ?? []}
      />
    </div>
  );
};
